import axios from "axios"
import { createBrowserHistory } from "history"
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import Tag from "./ui/tags";


let hasTokenExpired = false
export const setTokenExpired = (value) => {
  hasTokenExpired = value
}

export const getTokenExpired = () => hasTokenExpired

// Create a history object to allow navigation outside of a React component
export const history = createBrowserHistory()

/**
 * Makes a generalized API request using Axios with customizable options for 
 * URL, HTTP method, authentication, parameters, and error handling.
 * 
 * ---
 *
 * @param {Object} options - Configuration object for the API request.
 * @param {string} options.request - The requested API endpoint.
 * @param {Function} options.onSuccess - Callback function to handle a successful response. Receives response data as an argument.
 * @param {Function} options.onError - Optional callback for error handling, receives error as an argument.
 * @param {string} options.token - Optional authorization token, included in headers as `Bearer`.
 * @param {string} options.method - HTTP method for the request (e.g., "get", "post"). Default is "get".
 * @param {string} options.url - The backend base URL (defaults to the value in `process.env.REACT_APP_BACKEND_URL`).
 * @param {Object} options.params - Optional query parameters or request body data.
 * @param {Object} options.config - Additional optional Axios configuration (e.g., custom headers, timeout).
 * @param {Bool} options.returnSimple - Wether to return the error that is thrown
 *
 * ---
 * 
 * @returns {Promise<Object>} The response data if the request succeeds.
 * @throws Will throw an error if the request fails and rethrows if no custom `onError` is provided.
 */
export const callAPI = async ({ request, onError, onSuccess = null, token = localStorage.getItem("authToken"), method = "get", url = process.env.REACT_APP_BACKEND_URL, params = {}, config = {}}) => {
  if (getTokenExpired()) return null
  
  try {
    const response = await axios({
      url: `${url}/${request}`,
      method,
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        ...config.headers
      },
      params: method.toLowerCase() === "get" ? params : {},
      data: method.toLowerCase() !== "get" ? params : {},
      ...config
    })

    if (onSuccess) onSuccess?.(response.data)
    return response.data

  } catch (error) {
    console.log(error)
    if (error.response?.data?.errorMessage === "Token expired") {
      if (!getTokenExpired()) {
        setTokenExpired(true)
        localStorage.removeItem("authToken")
        console.warn("Your token has expired, logging you out")
        history.push("/login")
        window.location.reload();
      }
      return null
    }

    onError?.(error.response?.data?.errorMessage)
    return error
  }
}

/**
 * Verifies the authentication token stored in localStorage by making an API request.
 *
 * @returns {Object|null} The response data containing user information if the token is valid, or null if verification fails.
 */
export const verifyToken = async () => {
  const authToken = localStorage.getItem("authToken")
  if (!authToken) return null

  try {
    const response = await callAPI({
      request: "verifyUser",
      method: "get",
      token: authToken,
      onSuccess: (data) => data,
      onError: (error) => {
        if (error.response && error.response.status !== 403) throw error
        console.error("Error verifying token:", error.response?.data?.errorMessage)
      },
    })
    return response
  } catch (error) {
    console.error("Error during token verification:", error)
    return null
  }
}

/**
 * Decrypts and verifies the JWT token.
 *
 * @param {string} token - The JWT token to be decrypted.
 * @returns {Object} The decoded token payload.
 * @throws Will throw an error if the token is missing, invalid, or lacks required fields.
 */
export const decryptToken = () => {
  const token = localStorage.getItem("authToken")
  if (!token) throw new Error("No token provided");

  try {
    const decoded = jwtDecode(token);

    if (!decoded.exp) {
      throw new Error("Token does not contain an expiration field (exp)");
    }

    const now = Math.floor(Date.now() / 1000);

    if (decoded.exp < now) {
      throw new Error("Token is expired");
    }

    if (typeof decoded.roles === "string") {
      decoded.roles = decoded.roles.split(",").map((role) => role.trim());
    }

    return decoded;
  } catch (error) {
    throw new Error(`Invalid token: ${error.message}`);
  }
};

export const hasRoles = (...rolesToCheck) => {
  try {
    const decodedToken = decryptToken();
    const userRoles = Array.isArray(decodedToken.roles) 
      ? decodedToken.roles 
      : [decodedToken.roles];

    if (userRoles.includes("Owner")) return true;

    const hasRole = rolesToCheck.some(role => userRoles.includes(role));
    return hasRole;
    
  } catch (error) {
    console.error("Error checking roles:", error);
    return false;
  }
};



export const getMyStudents = async (handleError = null, setLoading = null) => {
  if (setLoading) setLoading(true);

  try {
    const response = await callAPI({
      request: "database/tutor/getMyStudents",
      onError: (error) => {
        console.error("Error fetching students:", error.response?.data?.errorMessage || "Unknown error");
        if (handleError) handleError("Error fetching students");
      },
    });

    return response?.data || [];
  } catch (error) {
    console.error("Failed to fetch data:", error);
    if (handleError) handleError("Failed to fetch data.");
    return [];
  } finally {
    if (setLoading) setLoading(false);
  }
};

export const getStudent = async (studentId, handleError = null) => {
  try {
    const response = await callAPI({
      request: "database/getStudent",
      params: { studentId },
      onError: (error) => { throw error; },
    });

    const data = response?.data || {};

    if (Object.keys(data).length === 0) {
      throw new Error("Failed to get student");
    }

    return data[0];
  } catch (error) {
    console.error("Error getting student:", error);
    if (handleError) handleError("Error getting student");
    throw error;
  }
};

export const validatePassword = (password) => {
  const minLength = 8
  const hasUppercase = /[A-Z]/.test(password)
  const hasLowercase = /[a-z]/.test(password)
  const hasNumber = /\d/.test(password)
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)

  if (password.length < minLength) {
    return "Password must be at least 8 characters long."
  }
  if (!hasUppercase) {
    return "Password must include at least one uppercase letter."
  }
  if (!hasLowercase) {
    return "Password must include at least one lowercase letter."
  }
  if (!hasNumber) {
    return "Password must include at least one number."
  }
  if (!hasSpecialChar) {
    return "Password must include at least one special character."
  }
  return ""
}

export const getTableFromDatabase = async (id, data = null, headers = null) => {
  const table = await callAPI({
    request: "getDataTables",
    method: "get",
    params: {tableId: id},
    token: localStorage.getItem("authToken"),
    onError: (error) => {throw error}
  })

  try {
    if ((!data && !headers) && table.data.apiLink != "manual"){

      const apiParams = JSON.parse(table.data.apiParams)
      const method = apiParams.method
      delete apiParams.method

      const tableData = await callAPI({
        request: table.data.apiLink,
        method: method,
        params: apiParams,
        token: localStorage.getItem("authToken"),
        onError: (error) => { throw error}
      })

      data = tableData.data.data
      headers = tableData.data.headers
    }

    return {
      data: data || [],
      headers: headers || [],
      title: table.data.title,
      noDataMsg: table.data.noDataMessage,
      modalTemplate: table.data.modalTemplate
    };
    

  } catch (error) {
    console.error("Error fetching table", error)
    return {
      data: [],
      headers: [],
      title: table.data.title,
      noDataMsg: table.data.noDataMessage,
      modalTemplate: table.data.modalTemplate
    };
  }
}

export const getSessionData = async (sessionId, handleError = null) => {
  try {
    const response = await callAPI({
      request: "sessions/getSession",
      params: { sessionId },
      onError: (error) => {
        console.error("Error fetching session details:", error.response?.data?.errorMessage || "Unknown error");
        if (handleError) handleError("Error fetching session details");
      },
    });

    return response?.data || {};
  } catch (error) {
    console.error("Failed to fetch session data:", error);
    if (handleError) handleError("Failed to fetch session data.");
    return {};
  }
};

export const extractLabels = (rows) => {
  return rows.map((row) => {
    const updatedRow = {};

    for (const [key, value] of Object.entries(row)) {
      if (Array.isArray(value)) {
        updatedRow[key] = value.map(item => {
          if (item?.label && item?.data?.colour) {
            return <Tag label={item.label} colour={item.data.colour} />;
          } else {
            return item?.label ?? item;
          }
        });
      } else if (value && typeof value === 'object' && value.label !== undefined) {
        if (value.data?.colour) {
          updatedRow[key] = <Tag label={value.label} colour={value.data.colour} />;
        } else {
          updatedRow[key] = value.label;
        }
      } else {
        updatedRow[key] = value;
      }
    }

    return updatedRow;
  });
};


// Input: 2025-02-09T22:05:13
// Output: Feb 9, 2025
export const formatDate = (timestamp) => {
  return moment(timestamp).format("MMM D, YYYY");
};

export const remapData = (data, fieldsConfig) => {
  const mappedValues = {};
  if (!data || !fieldsConfig) return mappedValues;

  fieldsConfig.sections.forEach(section => {
    section.fields.forEach(field => {
      if (field.type == "break") return
      const fieldId = field.id;
      const fieldKeys = fieldId.split('.');
      const fieldValue = data?.[fieldKeys[0]][fieldKeys[1]];

      if (!mappedValues[fieldKeys[0]]) {
        mappedValues[fieldKeys[0]] = {};
      }

      if ((fieldValue === undefined || fieldValue === "") || (fieldValue?.value && fieldValue?.label)) {
        return mappedValues[fieldKeys[0]][fieldKeys[1]] = fieldValue;
      }

      if (field.type === "multi-select" || field.type === "select") {
        if (Array.isArray(fieldValue) && fieldValue.length <= 0) return mappedValues[fieldKeys[0]][fieldKeys[1]] = fieldValue;
        let options;

        if (fieldValue[0]?.value && fieldValue[0]?.label) {
          options = fieldValue;
        } else if(Number.isInteger(fieldValue)){
          options = [{label: field.options?.find(opt => opt.value === fieldValue).label, value: fieldValue}]

        } else {
          options = fieldValue
            .split(",")
            .map(option => {
              const trimmedOption = option.trim();
              const matchedOption = field.options?.find(opt => opt.label === trimmedOption);
        
              return matchedOption ? { label: trimmedOption, value: matchedOption.value } : null;
            })
            .filter(option => option !== null);
        }
        
        return (mappedValues[fieldKeys[0]][fieldKeys[1]] = field.type === "multi-select" ? options : options[0]);
        
      }
        
      return mappedValues[fieldKeys[0]][fieldKeys[1]] = { label: fieldValue, value: fieldValue };
    });
  });

  return mappedValues;
};